import { Link } from 'react-router-dom';
import {
  Diamond,
  ArrowLeft,
  FileText,
  Scale,
  Lock,
  ShieldAlert,
  Check,
  X,
} from 'lucide-react';

const SECTIONS = [
  {
    id: 'digital-nature',
    icon: FileText,
    title: 'Digital Product Nature & Delivery',
    body: (
      <>
        <p>
          All products sold by{' '}
          <span className="font-semibold text-[#111]">Design Carat</span> are premium
          digital 3D models (STL/CAD files) intended for 3D printing and jewelry
          manufacturing.{' '}
          <span className="font-semibold text-[#111]">
            No physical items will be shipped to you.
          </span>
        </p>
        <p className="mt-4">
          Because we use a manual UPI verification system, your access link will be
          provided via{' '}
          <span className="font-semibold text-[#111]">WhatsApp or email</span> once
          your UTR number and payment have been successfully verified by our team.
        </p>
      </>
    ),
  },
  {
    id: 'licensing',
    icon: Scale,
    title: 'Licensing and Usage Rights',
    body: (
      <>
        <p>
          By purchasing a bundle from Design Carat, you are granted a{' '}
          <span className="font-semibold text-[#111]">standard commercial license</span>.
        </p>

        {/* MAY (allowed) panel */}
        <div
          className="mt-6 rounded-2xl p-5 sm:p-6"
          style={{
            background: 'linear-gradient(135deg, rgba(34,197,94,0.06) 0%, #FFF 100%)',
            border: '1px solid rgba(34,197,94,0.25)',
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <span
              className="flex items-center justify-center w-7 h-7 rounded-full"
              style={{ background: 'rgba(34,197,94,0.15)' }}
            >
              <Check className="w-4 h-4 text-[#16a34a]" />
            </span>
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#16a34a]">
              You MAY
            </span>
          </div>
          <ul className="space-y-2.5">
            {[
              '3D print the files to cast physical jewelry.',
              'Sell the physical, finished jewelry pieces you create using our designs.',
              'Use the files for personal or commercial physical manufacturing.',
            ].map((line, i) => (
              <li key={i} className="flex gap-3 font-body text-[14.5px] text-[#444]">
                <Check className="mt-0.5 w-4 h-4 text-[#16a34a] shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* MAY NOT (forbidden) panel */}
        <div
          className="mt-4 rounded-2xl p-5 sm:p-6"
          style={{
            background: 'linear-gradient(135deg, rgba(239,68,68,0.05) 0%, #FFF 100%)',
            border: '1px solid rgba(239,68,68,0.25)',
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <span
              className="flex items-center justify-center w-7 h-7 rounded-full"
              style={{ background: 'rgba(239,68,68,0.12)' }}
            >
              <X className="w-4 h-4 text-[#dc2626]" />
            </span>
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#dc2626]">
              You MAY NOT
            </span>
          </div>
          <ul className="space-y-2.5">
            {[
              'Resell, redistribute, share, or leak the original digital STL/CAD files.',
              'Modify the digital files to sell them as your own digital designs.',
              'Claim the digital designs as your own intellectual property.',
            ].map((line, i) => (
              <li key={i} className="flex gap-3 font-body text-[14.5px] text-[#444]">
                <X className="mt-0.5 w-4 h-4 text-[#dc2626] shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </>
    ),
  },
  {
    id: 'ip',
    icon: Lock,
    title: 'Intellectual Property',
    body: (
      <p>
        All 3D models, renders, images, and digital assets remain the{' '}
        <span className="font-semibold text-[#111]">
          sole intellectual property of Design Carat
        </span>
        . Any unauthorized distribution of our digital files will result in immediate
        termination of your access and potential legal action.
      </p>
    ),
  },
  {
    id: 'liability',
    icon: ShieldAlert,
    title: 'Limitation of Liability',
    body: (
      <p
        className="px-5 py-4 rounded-xl font-body text-[14.5px] text-[#444] leading-relaxed"
        style={{
          background: 'linear-gradient(135deg, #FFF7CD 0%, rgba(253,195,161,0.18) 100%)',
          border: '1px solid rgba(253,195,161,0.45)',
        }}
      >
        Design Carat is{' '}
        <span className="font-semibold text-[#111]">not responsible</span> for any
        manufacturing errors, casting failures, or material costs incurred during the
        physical production of these designs. It is your responsibility to verify the
        files in your slicing software before printing.
      </p>
    ),
  },
];

export default function TermsOfService() {
  return (
    <main
      data-testid="terms-of-service-page"
      className="min-h-screen"
      style={{ background: '#FFF7CD' }}
    >
      {/* ───────── Top bar ───────── */}
      <header
        className="sticky top-0 z-30"
        style={{
          background: 'rgba(255,247,205,0.85)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <div className="max-w-3xl mx-auto px-6 sm:px-8 h-14 flex items-center justify-between">
          <Link
            to="/"
            data-testid="terms-back-home"
            className="flex items-center gap-2 font-body text-sm text-[#555] hover:text-[#111] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <div className="flex items-center gap-2">
            <Diamond className="w-4 h-4 text-[#FDC3A1]" />
            <span className="font-heading font-bold text-[#111] text-sm tracking-tight">
              DesignCarat
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
        {/* ───────── Title block ───────── */}
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-body text-[10px] font-bold uppercase tracking-[0.22em]"
          style={{
            background: 'rgba(253,195,161,0.18)',
            border: '1px solid rgba(253,195,161,0.5)',
            color: '#c88a5e',
          }}
          data-testid="terms-eyebrow"
        >
          <FileText className="w-3 h-3" />
          Last Updated: May 2026
        </span>

        <h1
          data-testid="terms-heading"
          className="mt-6 font-heading font-black text-[#111] tracking-tight"
          style={{ fontSize: 'clamp(2.25rem, 5vw, 3.5rem)', lineHeight: 1.05 }}
        >
          Terms of Service
        </h1>
        <p className="mt-5 font-body text-base sm:text-lg text-[#666] leading-relaxed max-w-xl">
          Please review these terms carefully before purchasing the DesignCarat bundle.
          By completing checkout you agree to the terms outlined below.
        </p>

        {/* ───────── Decorative gold divider ───────── */}
        <div
          className="my-12 h-px"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgba(253,195,161,0.7), transparent)',
          }}
        />

        {/* ───────── Sections ───────── */}
        <div className="space-y-12">
          {SECTIONS.map(({ id, icon: Icon, title, body }, idx) => (
            <section key={id} data-testid={`terms-section-${id}`}>
              <div className="flex items-center gap-3 mb-5">
                <span
                  className="flex items-center justify-center w-9 h-9 rounded-full"
                  style={{
                    background: 'rgba(253,195,161,0.2)',
                    border: '1px solid rgba(253,195,161,0.5)',
                  }}
                >
                  <Icon className="w-4 h-4 text-[#c88a5e]" />
                </span>
                <span
                  className="font-mono text-[10px] font-bold uppercase tracking-[0.22em]"
                  style={{ color: '#c88a5e' }}
                >
                  Section {String(idx + 1).padStart(2, '0')}
                </span>
              </div>
              <h2
                className="font-heading font-bold text-[#111] tracking-tight mb-5"
                style={{ fontSize: 'clamp(1.5rem, 3.2vw, 2rem)' }}
              >
                {title}
              </h2>
              <div className="font-body text-[15px] sm:text-base text-[#444] leading-relaxed">
                {body}
              </div>
            </section>
          ))}
        </div>

        {/* ───────── Footer note ───────── */}
        <div className="mt-20 pt-8" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
          <p className="font-body text-xs text-[#999]">
            Questions about these terms? Email{' '}
            <a
              href="mailto:designcarat9@gmail.com"
              className="text-[#c88a5e] font-semibold hover:underline"
              data-testid="terms-footer-email"
            >
              designcarat9@gmail.com
            </a>{' '}
            and we&rsquo;ll get back within 24 hours.
          </p>
          <Link
            to="/"
            data-testid="terms-footer-back"
            className="mt-4 inline-flex items-center gap-2 font-body text-sm text-[#555] hover:text-[#111] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to DesignCarat
          </Link>
        </div>
      </div>
    </main>
  );
}
